/**
 * TrlImportModal — bring parts of another game's design into this one.
 *
 * Two stages. The admin picks a bundle file, then sees a checklist of exactly
 * what that file contains — nothing more — and unticks anything they want to
 * keep as it is. The preview is computed from the same plan the import runs on,
 * so the counts of skipped images, missing cartel rewards and dropped treasure
 * placements are what will actually happen, shown before anything is committed.
 *
 * Committing writes everything: the config sections through their own socket
 * events and the map through the builder's save. There is no half-applied state
 * left behind for the admin to remember to finish.
 */

import { useMemo, useState } from 'react';
import { Modal } from '@swtorbit/ui/react';
import {
  summarizeTrlBundleSection,
  type TrlBundleFile,
  type TrlBundleSection,
  type TrlImageAsset,
} from '@swtorbit/shared-socket-events';
import { parseBundleFile, listBundleSections, buildImportPlan, type TrlImportPlan } from './trlBundle';
import { TrlBundleChecklist, type TrlBundleChecklistRow } from './TrlBundleChecklist';

type Stage = 'choose' | 'preview' | 'importing';

interface TrlImportModalProps {
  /** This admin's content library, for re-linking the bundle's image refs. */
  images: TrlImageAsset[];
  /** True while the library is still loading — importing a map now would drop every image. */
  imagesLoading: boolean;
  /** Whether the current game already has a map (drives the replace warning). */
  hasExistingMap: boolean;
  /** Commit the plan. Resolves with an error message, or null on success. */
  onImport: (plan: TrlImportPlan) => Promise<string | null>;
  onClose: () => void;
}

export function TrlImportModal({
  images,
  imagesLoading,
  hasExistingMap,
  onImport,
  onClose,
}: TrlImportModalProps) {
  const [stage, setStage] = useState<Stage>('choose');
  const [file, setFile] = useState<TrlBundleFile | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [selected, setSelected] = useState<Set<TrlBundleSection>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const rows: TrlBundleChecklistRow[] = useMemo(() => {
    if (!file) return [];
    return listBundleSections(file.sections).map((id) => ({
      id,
      available: true,
      summary: summarizeTrlBundleSection(id, file.sections),
    }));
  }, [file]);

  // The plan drives both the preview below and the commit — one resolution, so
  // the admin can't be shown one outcome and handed another.
  const plan = useMemo(
    () => (file ? buildImportPlan(file, selected, images) : null),
    [file, selected, images],
  );

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    e.target.value = ''; // so re-picking the same file re-fires onChange
    if (!chosen) return;
    setError(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseBundleFile(String(ev.target?.result ?? ''));
        setFile(parsed);
        setFileName(chosen.name);
        setSelected(new Set(listBundleSections(parsed.sections)));
        setStage('preview');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to read that bundle.');
      }
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(chosen);
  };

  const toggle = (id: TrlBundleSection) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doImport = () => {
    if (!plan) return;
    setStage('importing');
    setError(null);
    void onImport(plan).then((failure) => {
      if (failure) {
        setError(failure);
        setStage('preview');
        return;
      }
      onClose();
    });
  };

  const busy = stage === 'importing';
  const replacingMap = selected.has('map') && hasExistingMap;
  const mapWithoutTreasure = selected.has('map') && rows.some((r) => r.id === 'treasure') && !selected.has('treasure');

  return (
    <Modal open onClose={onClose} dismissible={!busy} className="trl-bundle-modal" title="Import Design" footer={
<>
        <div className="d-flex gap-xs" style={{ marginLeft: 'auto' }}>
          {stage !== 'choose' && (
            <button
              type="button"
              className="btn btn--outline btn--slate"
              onClick={() => {
                setFile(null);
                setSelected(new Set());
                setError(null);
                setStage('choose');
              }}
              disabled={busy}
            >
              Choose another file
            </button>
          )}
          <button type="button" className="btn btn--outline btn--slate" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {stage !== 'choose' && (
            <button
              type="button"
              className="btn btn--green"
              onClick={doImport}
              disabled={busy || selected.size === 0}
            >
              {busy ? 'Importing…' : `Import ${selected.size > 0 ? `(${selected.size})` : ''}`}
            </button>
          )}
        </div>
      </>
}>
        <div className="d-flex flex-column gap-sm">
          {stage === 'choose' && (
            <>
              <p className="fs-xs text-slate m-0">
                Choose a Trailblazers bundle file. You&apos;ll see what&apos;s inside it and can
                pick which parts to bring into this game before anything changes.
              </p>
              {imagesLoading ? (
                <p className="fs-sm text-amber m-0">
                  Your content library is still loading — wait a moment, or the imported map will
                  lose every screenshot.
                </p>
              ) : (
                <input
                  type="file"
                  className="input"
                  accept=".json,application/json"
                  onChange={handleFile}
                />
              )}
            </>
          )}

          {stage !== 'choose' && file && (
            <>
              <p className="fs-xs text-slate m-0">
                <strong>{fileName}</strong>
                {file.sourceRoomCode ? ` · from game ${file.sourceRoomCode}` : ''}
                {` · exported ${new Date(file.exportedAt).toLocaleString()}`}
              </p>

              <TrlBundleChecklist
                rows={rows}
                selected={selected}
                onToggle={toggle}
                onSelectAll={() => setSelected(new Set(rows.map((r) => r.id)))}
                onSelectNone={() => setSelected(new Set())}
                disabled={busy}
              />

              {replacingMap && (
                <div className="bg-space bg-o-50 border-md border-amber rounded-base p-xs">
                  <span className="fs-sm text-amber">
                    This replaces the current galaxy map. The planets and paths in this game will be
                    gone.
                  </span>
                </div>
              )}

              {plan && (plan.skippedImages.length > 0 ||
                plan.skippedCartelItems.length > 0 ||
                plan.droppedTreasurePlacements > 0 ||
                mapWithoutTreasure) && (
                <div className="bg-space bg-o-50 border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-2xs">
                  {plan.skippedImages.length > 0 && (
                    <span className="fs-xs text-slate">
                      {plan.skippedImages.length} screenshot(s) aren&apos;t in your content library
                      and will be skipped: {plan.skippedImages.join(', ')}.
                    </span>
                  )}
                  {plan.skippedCartelItems.length > 0 && (
                    <span className="fs-xs text-slate">
                      {plan.skippedCartelItems.length} cartel reward(s) come from an older bundle
                      without SWTOR item links and will be skipped (re-add them with the item picker):{' '}
                      {plan.skippedCartelItems.join(', ')}.
                    </span>
                  )}
                  {mapWithoutTreasure && (
                    <span className="fs-xs text-slate">
                      The treasure list isn&apos;t selected, so the map&apos;s per-planet treasure
                      placements will be dropped. This game&apos;s own treasure list is left alone.
                    </span>
                  )}
                  {plan.droppedTreasurePlacements > 0 && !mapWithoutTreasure && (
                    <span className="fs-xs text-slate">
                      {plan.droppedTreasurePlacements} planet(s) lose their treasure placement
                      because the reward didn&apos;t survive the import.
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {error && <div className="p-xs rounded-xs bg-red text-white fs-sm">{error}</div>}
        </div>
      </Modal>
  );
}
